import { Globe, Handshake, Network, Lightbulb, Diamond } from 'lucide-react';
import { Reveal, SectionLabel } from './Reveal';
import { DIRECTORS, TEAM_INTRO, TEAM_PILLARS } from '../../data/content';
import patrickPhoto from '../../assets/patrick.png';
import pradeepPhoto from '../../assets/pradeep.png';

const PILLAR_ICONS = { globe: Globe, handshake: Handshake, network: Network, vision: Lightbulb };
const PHOTOS = { patrick: patrickPhoto, pradeep: pradeepPhoto };

const DirectorCard = ({ d }) => (
  <div className="group flex h-full flex-col border border-white/10 bg-navy-950 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10">
    <div className="flex items-center gap-5">
      {d.photo ? (
        <img
          src={PHOTOS[d.photo]}
          alt={d.name}
          className="h-24 w-20 shrink-0 border border-gold/50 object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
          style={{ objectPosition: '50% 20%' }}
        />
      ) : (
        <span className="flex h-24 w-20 shrink-0 items-center justify-center border border-gold/50 font-serif text-2xl text-gold">
          {d.initials}
        </span>
      )}
      <div>
        <h3 className="font-serif text-2xl leading-tight tracking-tight text-white lg:text-3xl">
          {d.name}
        </h3>
        <span className="mt-1 block text-xs font-bold uppercase tracking-[0.24em] text-gold">
          {d.title}
        </span>
      </div>
    </div>

    <p className="mt-7 text-base leading-relaxed text-slate-300">{d.summary}</p>

    <ul className="mt-6 space-y-4">
      {d.highlights.map((h, i) => (
        <li key={i} className="flex gap-3">
          <Diamond className="mt-1.5 h-3 w-3 shrink-0 text-gold/70" strokeWidth={2} />
          <span className="text-sm leading-relaxed text-slate-400">{h}</span>
        </li>
      ))}
    </ul>

    <div className="mt-auto pt-8">
      <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
        Areas of Expertise
      </span>
      <div className="mt-4 flex flex-wrap gap-2">
        {d.expertise.map((e) => (
          <span
            key={e}
            className="border border-white/10 px-3 py-1.5 text-xs text-slate-300 transition-colors duration-300 group-hover:border-gold/30"
          >
            {e}
          </span>
        ))}
      </div>
    </div>
  </div>
);

export const Team = () => (
  <section
    id="team"
    data-testid="team-section"
    className="relative border-t border-white/5 bg-navy-950 py-24 lg:py-32"
  >
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="max-w-3xl">
        <Reveal>
          <SectionLabel>Introduction to Team</SectionLabel>
          <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
            The people behind the mandate.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-slate-300 lg:text-lg">
            {TEAM_INTRO}
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {DIRECTORS.map((d, i) => (
          <Reveal key={d.name} delay={i * 0.1}>
            <DirectorCard d={d} />
          </Reveal>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {TEAM_PILLARS.map((p, i) => {
          const Icon = PILLAR_ICONS[p.icon];
          return (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="h-full bg-navy-900 p-8">
                <Icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
                <h4 className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-gold">
                  {p.title}
                </h4>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">
                  {p.text}
                </p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
